import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../../../parts/modal-new";
import ProductChip from "../automations/ProductChip";
import { registerPublishGate } from "./save-changes";

const id = "modal-publish-changes";
const CHOICES = ["new_only", "everyone"];
const CHOICE_KEYS = { new_only: "newOnly", everyone: "everyone" };

/**
 * Asks "Who should get the updated version?" when the steps of an automation that is on change
 * and people are part-way through it: the new entries only (the default) or everyone. It stands
 * in the way of `saveChanges`, holds the change while the owner answers, and then saves it.
 */
const PublishGate = ({ automation, peopleInside }) => {
  const { t } = useTranslation("coaching-portal");
  const tr = (key, params) => t(`emailAutomations.publish.${key}`, params);
  const [pending, setPending] = useState(null);
  const [apply, setApply] = useState("new_only");
  const asks = automation.status === "active" && peopleInside > 0;

  useEffect(
    () =>
      registerPublishGate({
        needsAsk: (changes) => asks && "steps" in changes,
        ask: (change) => {
          setApply("new_only");
          setPending(change);
        },
      }),
    [asks]
  );

  if (!pending) return null;
  const close = (event) => {
    event?.preventDefault();
    setPending(null);
  };

  return (
    <form
      action={`/actions/automations/${pending.automationId}/save`}
      method="POST"
      data-noreset
    >
      <input
        type="hidden"
        name="changes"
        value={JSON.stringify(pending.changes)}
      />
      {pending.returnTo ? (
        <input type="hidden" name="returnTo" value={pending.returnTo} />
      ) : null}
      <input type="hidden" name="apply" value={apply} />
      <Modal
        name={id}
        key={id}
        className="ea-modal ea-modal--publish"
        onClose={close}
        customTitle={
          <h2 className="ea-modal__title">
            {tr("title")}
            <ProductChip product={automation.product} />
          </h2>
        }
      >
        <div className="body">
          <p className="ea-modal__lead">
            {tr("lead", { count: peopleInside })}
          </p>
          <fieldset className="ea-choices">
            <legend className="sr-only">{tr("title")}</legend>
            {CHOICES.map((choice) => (
              <label
                key={choice}
                className={`ea-choice${apply === choice ? " ea-choice--selected" : ""}`}
              >
                <input
                  type="radio"
                  name="who"
                  value={choice}
                  className="sr-only"
                  checked={apply === choice}
                  onChange={() => setApply(choice)}
                />
                <i className="ea-choice__radio" aria-hidden="true" />
                <strong>{tr(`${CHOICE_KEYS[choice]}.title`)}</strong>
                <span>{tr(`${CHOICE_KEYS[choice]}.body`)}</span>
              </label>
            ))}
          </fieldset>
        </div>
        <footer className="foot">
          <button
            type="button"
            data-button-type="secondary"
            data-action="cancel"
            onClick={close}
          >
            {tr("cancel")}
          </button>
          <button type="submit" data-button-type="primary">
            {tr("confirm")}
          </button>
        </footer>
      </Modal>
    </form>
  );
};

PublishGate.propTypes = {
  automation: PropTypes.shape({
    status: PropTypes.string,
    product: PropTypes.string.isRequired,
  }).isRequired,
  peopleInside: PropTypes.number,
};

export default PublishGate;
